import type { LazyComponentPromise } from '../lazy.types';

import { isChunkLoadError } from './isChunkLoadError';

const DEFAULT_RETRIES = 2;
const DEFAULT_BASE_DELAY_MS = 250;

export interface RetryImportOptions {
  /**
   * Number of additional attempts after the first failure. Defaults to {@link DEFAULT_RETRIES}.
   */
  readonly retries?: number;

  /**
   * Delay before the first retry, in milliseconds. Doubles on every subsequent retry. Defaults to
   * {@link DEFAULT_BASE_DELAY_MS}.
   */
  readonly baseDelayMs?: number;
}

const delay = (durationMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, durationMs);
  });

/**
 * Wraps a dynamic-import loader so transient chunk load failures (a momentary CDN/network blip)
 * are retried with exponential backoff before the rejection reaches React.lazy. Non chunk-load
 * errors are surfaced immediately so genuine module errors are not masked or retried.
 */
export const retryImport = async <T>(
  loader: () => LazyComponentPromise<T>,
  options: RetryImportOptions = {}
): LazyComponentPromise<T> => {
  const { retries = DEFAULT_RETRIES, baseDelayMs = DEFAULT_BASE_DELAY_MS } = options;

  try {
    return await loader();
  } catch (error) {
    if (!isChunkLoadError(error) || retries <= 0) {
      throw error;
    }

    await delay(baseDelayMs);

    return await retryImport(loader, { retries: retries - 1, baseDelayMs: baseDelayMs * 2 });
  }
};
